import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  User, Building2, Phone, ShieldCheck, Sparkles, MapPin, Shield,
  Plug, CreditCard, FileText, Users, Lock, Bell, Voicemail as VoicemailIcon,
} from 'lucide-react';
import TopBar from '../components/TopBar.jsx';
import { useAuth } from '../store/auth.js';
import { apiGet } from '../lib/api.js';

import ProfileTab from './settings/ProfileTab.jsx';
import TenantTab from './settings/TenantTab.jsx';
import PhoneNumbersTab from './settings/PhoneNumbersTab.jsx';
import VoicemailDropTab from './settings/VoicemailDropTab.jsx';
import A2PTab from './settings/A2PTab.jsx';
import LeadSourcesTab from './settings/LeadSourcesTab.jsx';
import LeadVendorsTab from './settings/LeadVendorsTab.jsx';
import GeoTab from './settings/GeoTab.jsx';
import ComplianceTab from './settings/ComplianceTab.jsx';
import IntegrationsTab from './settings/IntegrationsTab.jsx';
import BillingTab from './settings/BillingTab.jsx';
import AuditLogTab from './settings/AuditLogTab.jsx';
import SecurityTab from './settings/SecurityTab.jsx';
import NotificationsTab from './settings/NotificationsTab.jsx';
import BookingPageTab from './settings/BookingPageTab.jsx';
import DigitalBusinessCardSettings from './settings/DigitalBusinessCardSettings.jsx';
import Team from './Team.jsx';
import MyTeamPanel from '../components/team/MyTeamPanel.jsx';
import SupervisorSection from './settings/SupervisorSection.jsx';
import ProducerPageTab from './settings/ProducerPageTab.jsx';
import { Calendar, Tag, BadgeCheck, Pin, PinOff } from 'lucide-react';
import RailTip from '../components/layout/RailTip.jsx';
import { usePersistedFlag } from '../hooks/usePersistedFlag.js';

// Tab definitions. `adminOnly` mirrors the server-side requireRole gate —
// hiding the tab in the UI is convenience, the server enforces the policy.
/**
 * "My Team" tab. Everyone gets My Supervisor (if they have one) and My Team (their
 * downline); tenant admins additionally keep the existing Team page (invites, approvals, roster).
 */
function TeamTab() {
  const { agent } = useAuth();
  const isAdmin = ['admin', 'superadmin'].includes(agent?.role);
  return (
    <div className="space-y-6">
      <SupervisorSection />
      <MyTeamPanel />
      {isAdmin && <Team />}
    </div>
  );
}

const TABS = [
  { id: 'profile', label: 'Profile', icon: User, Component: ProfileTab },
  { id: 'business-card', label: 'Digital Business Card', icon: Tag, Component: DigitalBusinessCardSettings },
  { id: 'booking', label: 'Booking Page', icon: Calendar, Component: BookingPageTab },
  { id: 'notifications', label: 'Notifications', icon: Bell, Component: NotificationsTab },
  { id: 'security', label: 'Security', icon: Lock, Component: SecurityTab },
  { id: 'team', label: 'My Team', icon: Users, Component: TeamTab },
  { id: 'producer-page', label: 'Producer Page', icon: BadgeCheck, Component: ProducerPageTab },
  { id: 'tenant', label: 'Tenant', icon: Building2, Component: TenantTab, adminOnly: true },
  { id: 'phone', label: 'Phone Numbers', icon: Phone, Component: PhoneNumbersTab },
  { id: 'voicemail', label: 'VoiceMail Drop', icon: VoicemailIcon, Component: VoicemailDropTab },
  { id: 'a2p', label: 'A2P Compliance', icon: ShieldCheck, Component: A2PTab, adminOnly: true },
  { id: 'leads', label: 'Lead Sources', icon: Sparkles, Component: LeadSourcesTab, adminOnly: true },
  { id: 'lead-vendors', label: 'Lead Vendors', icon: Users, Component: LeadVendorsTab, adminOnly: true },
  { id: 'geo', label: 'Geo-Restrictions', icon: MapPin, Component: GeoTab, adminOnly: true },
  { id: 'compliance', label: 'Compliance & DNC', icon: Shield, Component: ComplianceTab, adminOnly: true },
  { id: 'integrations', label: 'Integrations', icon: Plug, Component: IntegrationsTab, adminOnly: true },
  { id: 'billing', label: 'Billing', icon: CreditCard, Component: BillingTab, adminOnly: true },
  { id: 'audit', label: 'Audit Log', icon: FileText, Component: AuditLogTab, adminOnly: true },
];

export default function Settings() {
  const { agent } = useAuth();
  const isAdmin = ['admin', 'superadmin'].includes(agent?.role);
  const [params, setParams] = useSearchParams();
  const [tabsPinned, toggleTabs] = usePersistedFlag('civix_settings_tabs_pinned', false);
  const [tabsHover, setTabsHover] = useState(false);
  const tabsCollapsed = !(tabsPinned || tabsHover);

  // Shares the key used by Layout / Dashboard, so the count stays in sync live.
  const { data: pendingData } = useQuery({
    queryKey: ['pending-teammate-approvals'],
    queryFn: () => apiGet('/team/pending-approvals'),
    enabled: isAdmin,
  });
  const pendingApprovalsCount =
    pendingData?.pendingApprovalCount ?? (pendingData?.items || pendingData?.pendingAgents || []).length;

  const visibleTabs = useMemo(
    () => TABS.filter((t) => !t.adminOnly || isAdmin),
    [isAdmin]
  );

  // Accept ?section=… as an alias for ?tab=… so deep links from PhoneSetup
  // (e.g. /settings?section=phone) still land on the right tab after the
  // tabbed-UI rebuild.
  //
  // Also accept the sidebar-label slugs as aliases for the canonical IDs so
  // bookmarks written from the sidebar label text still resolve (audit
  // finding 2026-08-25: users bookmarking Compliance & DNC as ?tab=compliance-dnc
  // silently fell back to Profile).
  const TAB_ALIASES = {
    'vcard': 'business-card',
    'vcard-scheduler': 'business-card',
    'digital-business-card': 'business-card',
    'business-card': 'business-card',
    'booking-page': 'booking',
    'appointment-booking': 'booking',
    'booking': 'booking',
    'phone-numbers': 'phone',
    'voicemail': 'voicemail',
    'voicemail-drop': 'voicemail',
    'voicemails': 'voicemail',
    'vm-drop': 'voicemail',
    'a2p-compliance': 'a2p',
    'lead-sources': 'leads',
    'geo-restrictions': 'geo',
    'compliance-dnc': 'compliance',
    'audit-log': 'audit',
    'producer-page': 'producer-page',
    'producer': 'producer-page',
    'producer-validation-page': 'producer-page',
    'team': 'team',
    'my-team': 'team',
    'team-management': 'team',
  };
  const rawInitial = params.get('tab') || params.get('section') || 'profile';
  const initial = TAB_ALIASES[rawInitial] || rawInitial;
  const validInitial = visibleTabs.find((t) => t.id === initial)?.id || visibleTabs[0]?.id || 'profile';
  const [active, setActive] = useState(validInitial);

  useEffect(() => {
    if (params.get('tab') !== active) {
      setParams({ tab: active }, { replace: true });
    }
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const ActiveComponent = visibleTabs.find((t) => t.id === active)?.Component || ProfileTab;

  return (
    <div className="space-y-4">
      <TopBar
        title="Settings"
        subtitle={isAdmin ? 'Account · Tenant · Compliance' : 'Account'}
      />

      <div className={`grid gap-4 transition-all duration-300 ease-in-out ${!tabsPinned ? 'lg:grid-cols-[56px_1fr]' : 'lg:grid-cols-[240px_1fr]'}`}>
        <div className="relative min-w-0 lg:z-30" onMouseEnter={() => setTabsHover(true)} onMouseLeave={() => setTabsHover(false)}>
        <aside className={`panel p-2 h-fit lg:sticky lg:top-4 overflow-x-auto lg:overflow-visible transition-all duration-300 ease-in-out ${!tabsPinned && tabsHover ? 'lg:absolute lg:top-0 lg:left-0 lg:w-60 lg:shadow-2xl lg:backdrop-blur-md lg:bg-civix-panel/95' : ''}`} data-collapsed={tabsCollapsed}>
          <button
            type="button"
            onClick={toggleTabs}
            aria-label={tabsPinned ? 'Unpin settings menu' : 'Pin settings menu open'}
            aria-pressed={tabsPinned}
            data-testid="settings-tabs-toggle"
            className={`hidden lg:flex mb-1 w-full items-center gap-2 px-2 py-1.5 rounded-md text-xs text-white/50 hover:text-white hover:bg-white/5 transition ${tabsCollapsed ? 'lg:hidden' : 'justify-end'}`}
          >
            {tabsPinned ? <Pin className="w-4 h-4 text-civix-purpleSoft" /> : <PinOff className="w-4 h-4" />}
          </button>
          <nav className="flex lg:flex-col gap-0.5 min-w-max lg:min-w-0">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              const isActive = active === t.id;
              const badge = t.id === 'team' ? pendingApprovalsCount : 0;
              return (
                <RailTip key={t.id} label={badge > 0 ? `${t.label} · ${badge} pending` : t.label} enabled={tabsCollapsed} className="lg:w-full">
                  <button
                    onClick={() => setActive(t.id)}
                    aria-label={t.label}
                    className={`relative flex w-full items-center gap-2 py-2 rounded-md text-sm transition whitespace-nowrap ${tabsCollapsed ? 'lg:justify-center lg:px-0 px-3' : 'px-3'} ${
                      isActive
                        ? 'bg-civix-purple/20 text-white border border-civix-purple/40'
                        : 'text-white/65 hover:text-white hover:bg-white/5 border border-transparent'
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className={tabsCollapsed ? 'lg:hidden' : ''}>{t.label}</span>
                    {badge > 0 && (
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none bg-rose-600 text-white animate-pulse ${tabsCollapsed ? 'ml-auto lg:ml-0 lg:absolute lg:top-0.5 lg:right-0.5 lg:w-2.5 lg:h-2.5 lg:p-0 lg:text-[0px]' : 'ml-auto'}`}
                        aria-label={`${badge} pending approvals`}
                        data-testid="team-tab-badge"
                      >
                        {badge}
                      </span>
                    )}
                  </button>
                </RailTip>
              );
            })}
          </nav>
        </aside>
        </div>

        <main className="min-w-0">
          <ActiveComponent />
        </main>
      </div>
    </div>
  );
}
